import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useStatementStore } from "../store/statementStore";
import { exportPdfUrl, downloadExportedPdf } from "../api/engine";
import TransactionTable from "../components/TransactionTable";
import FreeTextEditor from "../components/FreeTextEditor";
import ExportProgressBar from "../components/ExportProgressBar";

function fmt(num, f) {
    if (num == null || !f) return "—";
    if (typeof num === "string") return num;
    if (typeof num !== "number" || !Number.isFinite(num)) return "—";
    const sign = num < 0;
    const abs = Math.abs(num);
    const parts = abs.toFixed(f.decimals ?? 2).split(".");
    if (f.has_thousands) {
        if (f.indian_grouping && parts[0].length > 3) {
            const last3 = parts[0].slice(-3);
            let rest = parts[0].slice(0, -3);
            const groups = [];
            while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
            if (rest) groups.unshift(rest);
            parts[0] = groups.join(",") + "," + last3;
        } else {
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    }
    let out = parts.join(".");
    if (sign) out = f.paren_negative ? `(${out})` : `-${out}`;
    return (f.prefix || "") + out + (f.suffix || "");
}

export default function WorkspacePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { statement, load, resetAll, flushing, prepareExport, exporting, exportMessage, exportProgress, pendingMetadataOverride, setMetadataOverride, flushEdits } = useStatementStore();
    const [activeTab, setActiveTab] = useState("transactions"); // "transactions" | "text"

    useEffect(() => {
        if (!statement || statement.id !== id) load(id);
    }, [id, statement, load]);

    const stats = useMemo(() => {
        if (!statement) return null;
        const txns = statement.transactions || [];
        const edited = txns.filter((t) => t.user_edited).length;
        const textEdited = txns.filter((t) => t.text_edited).length;
        const dirty = txns.filter((t) => t.balance_dirty).length;
        const freeEdited = (statement.free_text_spans || []).filter((s) => s.is_edited).length;
        const totalDebit = txns.reduce((s, t) =>
            s + (t.edited_debit != null ? t.edited_debit : t.debit != null ? t.debit : 0), 0);
        const totalCredit = txns.reduce((s, t) =>
            s + (t.edited_credit != null ? t.edited_credit : t.credit != null ? t.credit : 0), 0);
        return { edited, textEdited, dirty, freeEdited, totalDebit, totalCredit, count: txns.length };
    }, [statement]);

    async function handleExport(e, format = null) {
        if (e && e.preventDefault) e.preventDefault();
        try {
            await prepareExport();
            await downloadExportedPdf(id, statement?.filename, format);
        } catch {
            // Store surfaces the error in the shared error state.
        }
    }

    if (!statement) {
        return (
            <div className="min-h-screen grid place-items-center text-zinc-500 font-mono text-xs">
                Loading statement...
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-[#0b0c0e] text-zinc-100">
            {/* Top app bar */}
            <header className="h-14 border-b border-white/[0.06] flex items-center justify-between px-6 bg-[#0f1013]/80 backdrop-blur sticky top-0 z-30">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate("/")}
                        className="text-zinc-400 hover:text-zinc-100 transition-colors p-1.5 -ml-1.5 rounded-md hover:bg-white/[0.04]"
                        title="Back to statements"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div className="w-2 h-2 rounded-full bg-emerald-400/80 shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                    <span className="font-medium text-[13px] tracking-tight text-zinc-200">FinPDF Studio</span>
                    <span className="text-zinc-600">/</span>
                    <span className="text-[13px] text-zinc-400 font-mono truncate max-w-xs">{statement.filename}</span>
                    <div className="flex items-center gap-1.5 ml-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-400 px-2 py-0.5 rounded-full bg-white/[0.03] border border-white/[0.06]">
                            {statement.pages} page{statement.pages > 1 ? "s" : ""} · {statement.transactions.length} transactions{statement.currency ? ` · ${statement.currency}` : ""}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button className="btn-ghost rounded-md px-3 py-1.5 text-[12px] font-mono" onClick={() => resetAll()} disabled={flushing} data-testid="reset-all-btn">
                        Reset all
                    </button>
                    <button className="btn-ghost rounded-md px-3 py-1.5 text-[12px] font-mono" onClick={() => navigate(`/compare/${id}`)} data-testid="open-compare-btn">
                        Side-by-side
                    </button>
                    <a href={exportPdfUrl(id)} onClick={(e) => handleExport(e)} className="btn-primary rounded-md px-3 py-1.5 text-[12px] font-medium min-w-[100px] text-center" data-testid="export-pdf-btn">
                        {exporting ? `${Math.round(exportProgress || 0)}%` : "Export PDF"}
                    </a>
                </div>
            </header>
            <ExportProgressBar
                active={exporting}
                message={exportMessage}
                progress={exportProgress}
            />

            <div className="grid grid-cols-12 gap-0 h-[calc(100vh-57px)] overflow-hidden">
                {/* Left column: metadata */}
                <aside className="col-span-3 border-r border-white/[0.06] p-6 space-y-6 overflow-y-auto">
                    <SectionLabel>Document</SectionLabel>
                    <KeyValue k="File" v={statement.filename} mono />
                    <KeyValue k="Pages" v={String(statement.pages)} mono />
                    <KeyValue k="Transactions" v={String(statement.transactions.length)} mono />
                    <KeyValue k="Currency" v={statement.currency || "—"} mono />

                    <SectionLabel>Detected columns</SectionLabel>
                    <div className="grid grid-cols-2 gap-2">
                        {Object.entries(statement.columns || {}).map(([k, v]) =>
                            v && (
                                <div key={k} className="px-3 py-2 rounded border border-white/[0.06] bg-black/30">
                                    <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500">{k}</div>
                                    <div className="text-[11px] font-mono text-zinc-200 mt-0.5">{v[0].toFixed(0)}–{v[1].toFixed(0)}</div>
                                </div>
                            )
                        )}
                    </div>

                    <SectionLabel>Number format</SectionLabel>
                    <div className="space-y-1.5 text-[11px] font-mono text-zinc-400">
                        <div>decimals: <span className="text-zinc-200">{statement.number_format.decimals}</span></div>
                        <div>thousands: <span className="text-zinc-200">{String(statement.number_format.has_thousands)}</span></div>
                        <div>indian grouping: <span className="text-zinc-200">{String(statement.number_format.indian_grouping)}</span></div>
                    </div>

                    <SectionLabel>Metadata Spoofing</SectionLabel>
                    <div className="space-y-3">
                        {['producer', 'creator', 'creationDate', 'modDate'].map(field => {
                            const val = pendingMetadataOverride?.[field] ?? statement?.metadata_override?.[field] ?? "";
                            return (
                                <div key={field}>
                                    <div className="text-[10px] text-zinc-400 capitalize mb-1">{field === 'modDate' ? 'Modification Date' : field === 'creationDate' ? 'Creation Date' : field}</div>
                                    <input
                                        className="w-full bg-black/40 border border-white/[0.06] rounded px-2 py-1 text-[11px] font-mono text-zinc-200 outline-none focus:border-violet-500/50 focus:bg-violet-500/5 transition-colors"
                                        placeholder="Clear or enter custom value"
                                        value={val}
                                        onChange={(e) => setMetadataOverride(field, e.target.value)}
                                        onBlur={flushEdits}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* Center: tabs + content */}
                <main className="col-span-6 min-w-0 flex flex-col border-r border-white/[0.06] overflow-hidden">
                    {/* Tab bar */}
                    <div className="flex border-b border-white/[0.06] bg-black/20">
                        <TabBtn
                            active={activeTab === "transactions"}
                            onClick={() => setActiveTab("transactions")}
                            badge={stats?.edited + stats?.textEdited || null}
                        >
                            Transactions
                        </TabBtn>
                        <TabBtn
                            active={activeTab === "text"}
                            onClick={() => setActiveTab("text")}
                            badge={stats?.freeEdited || null}
                            accent="violet"
                        >
                            Text Editor
                            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 font-mono uppercase tracking-wide">
                                NEW
                            </span>
                        </TabBtn>
                    </div>

                    <div className="flex-1 min-h-0 min-w-0 p-6 overflow-auto">
                        {activeTab === "transactions" && (
                            <>
                                <div className="mb-5 flex items-center justify-between">
                                    <div>
                                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-1">
                                            Workspace · transactions
                                        </div>
                                        <h2 className="text-[20px] tracking-tight">Inline edit. Live recalculation.</h2>
                                    </div>
                                    {flushing && (
                                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500" data-testid="flushing-indicator">
                                            <div className="live-dot" />
                                            Recalculating
                                        </div>
                                    )}
                                </div>
                                <TransactionTable />
                                <div className="mt-4 text-[10px] font-mono text-zinc-500 space-y-1">
                                    <div><span className="text-amber-300">●</span> Highlighted balances were recomputed after your edits.</div>
                                    <div><span className="text-violet-400">●</span> Highlighted text (cheque/ref, description, date) edited in-place with original font preserved.</div>
                                </div>
                            </>
                        )}

                        {activeTab === "text" && (
                            <div>
                                <div className="mb-5">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-1">
                                        Workspace · free-text editor
                                    </div>
                                    <h2 className="text-[20px] tracking-tight">Edit any text. Zero font drift.</h2>
                                </div>
                                {flushing && (
                                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-4" data-testid="flushing-indicator">
                                        <div className="live-dot" />
                                        Saving…
                                    </div>
                                )}
                                <FreeTextEditor />
                            </div>
                        )}
                    </div>
                </main>

                {/* Right: live ledger card */}
                <aside className="col-span-3 border-l border-white/[0.06] p-6 space-y-6 overflow-y-auto">
                    <SectionLabel>Live ledger</SectionLabel>

                    <Metric label="Opening Balance" value={statement.display_opening_balance ?? fmt(statement.opening_balance, statement.number_format)} testId="metric-opening-balance" />
                    <Metric label="Closing Balance" value={statement.display_closing_balance ?? fmt(statement.closing_balance, statement.number_format)}
                        accent={stats?.dirty > 0 ? "text-amber-300" : "text-white"} testId="metric-closing-balance" />

                    <div className="grid grid-cols-2 gap-3">
                        <SubMetric label="Σ Debit" value={fmt(stats?.totalDebit, statement.number_format)} tone="debit" testId="metric-total-debit" />
                        <SubMetric label="Σ Credit" value={fmt(stats?.totalCredit, statement.number_format)} tone="credit" testId="metric-total-credit" />
                    </div>

                    <div className="divider-h pt-5">
                        <SectionLabel>Activity</SectionLabel>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <SubMetric label="Edited rows" value={String(stats?.edited ?? 0)} testId="metric-edited" />
                            <SubMetric label="Dirty balances" value={String(stats?.dirty ?? 0)} tone="warn" testId="metric-dirty" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <SubMetric label="Tx text edits" value={String(stats?.textEdited ?? 0)} tone="text" testId="metric-text-edited" />
                            <SubMetric label="Free edits" value={String(stats?.freeEdited ?? 0)} tone="violet" testId="metric-free-edited" />
                        </div>
                    </div>

                    {/* Quick tip */}
                    <div className="divider-h pt-5">
                        <div className="px-3 py-3 rounded border border-violet-500/20 bg-violet-500/[0.03] text-[10px] font-mono text-zinc-500 leading-relaxed">
                            <span className="text-violet-400 font-medium">NEW:</span> Switch to{" "}
                            <button onClick={() => setActiveTab("text")} className="text-violet-300 hover:underline">
                                Text Editor
                            </button>{" "}
                            tab to change names, addresses &amp; any PDF text while preserving font/size exactly.
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function TabBtn({ children, active, onClick, badge, accent }) {
    const accentClass = accent === "violet"
        ? active ? "border-violet-500 text-violet-300" : "text-zinc-500 hover:text-zinc-300"
        : active ? "border-blue-500 text-white" : "text-zinc-500 hover:text-zinc-300";
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-5 py-3.5 text-[12px] font-mono border-b-2 transition-colors ${
                active ? `${accentClass} bg-white/[0.02]` : `border-transparent ${accentClass}`
            }`}
        >
            {children}
            {badge > 0 && (
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${
                    accent === "violet" ? "bg-violet-500/20 text-violet-400" : "bg-amber-500/20 text-amber-400"
                }`}>
                    {badge}
                </span>
            )}
        </button>
    );
}

function SectionLabel({ children }) {
    return <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">{children}</div>;
}

function KeyValue({ k, v, mono }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">{k}</span>
            <span className={`text-[12px] ${mono ? "font-mono" : ""} text-zinc-200 truncate max-w-[160px]`} title={v}>{v}</span>
        </div>
    );
}

function Metric({ label, value, accent = "text-white", testId }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-1.5">{label}</div>
            <motion.div
                key={value}
                initial={{ y: -3, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className={`text-[24px] font-mono font-light tracking-tighter ${accent}`}
                data-testid={testId}
            >
                {value}
            </motion.div>
        </div>
    );
}

function SubMetric({ label, value, tone, testId }) {
    const toneClass =
        tone === "credit" ? "text-emerald-400"
        : tone === "debit" ? "text-red-400"
        : tone === "warn" ? "text-amber-300"
        : tone === "text" ? "text-violet-400"
        : tone === "violet" ? "text-violet-300"
        : "text-zinc-200";
    return (
        <div className="px-3 py-2.5 rounded border border-white/[0.06] bg-black/30">
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className={`text-[14px] font-mono mt-1 ${toneClass}`} data-testid={testId}>{value}</div>
        </div>
    );
}
